/*!

Fast 2D text rendering based on `thomas`, a multichannel signed distance field library from flux.ai

https://github.com/buildwithflux/thomas

MIT License

Copyright (c) 2023 Flux
Copyright (c) 2025 Paragon Component Systems, LLC

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

*/

/*
  Changes made in our fork:
    - updated `FontLoader` to `three-stdlib` version
    - removed hard-coded render order numbers
    - allowed setting `depthTest` on text material
    - added `faceCamera` option to auto-rotate text to face our camera
    - allowed hiding all text in a provider without unregistering its instances
    - allowed multiplying outline opacity at the provider level
    - optimized transform updates by only recalculating only dirty transforms and batching geometry updates
    - added calls to `invalidate` after geometry, transform, and visibility updates to support demand frame loop
    - removed placeholder Object3Ds to cut out three.js render-time matrix updates; requiring an explict matrix world
    - skipped transform updates while hidden or when not required to further reduce three.js render-time work
    - added output color-space conversion so SDF text colors match standard Three.js materials
    - registers segments in a mutable set so subscribing / unsubscribing doesn't trigger state changes
*/

import { extend, ThreeElement } from '@react-three/fiber';

import MSDFTextGeometry from './MSDFTextGeometry';
import MSDFTextMaterial from './MSDFTextMaterial';

export function registerTextEngineToR3F() {
  extend({ MSDFTextMaterial, MSDFTextGeometry });
}

declare module '@react-three/fiber' {
  interface ThreeElements {
    mSDFTextMaterial: ThreeElement<typeof MSDFTextMaterial>;
    mSDFTextGeometry: ThreeElement<typeof MSDFTextGeometry>;
  }
}
